<template>
    <div class="synchronized-video-controls">
        <player-navigation
            :play-state="playState"
            :video-seek-mode="videoSeekMode"
            @playToggled="playHandler"
            @skipClicked="skip"
        />
    </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import {
    ALL_DAYS,
    ALL_ISTANBUL_EVENTS,
    TREATMENT_PAGE_VIEWS,
    TREATMENT_STATUS,
    VIDEO_SEEK_MODES
} from '../../../business/const'
import Utils from '../../../business/utils/Utils'
import VideoSeekModeSelector from './VideoSeekModeSelector'
import PlayerNavigation from '../video/PlayerNavigation'
import TimeSkipAmountHandler from '../video/TimeSkipAmountHandler'
import EventSkipAmountHandler from '../video/EventSkipAmountHandler'

export default {
    name: 'SynchronizedVideoControls',
    components: {
        PlayerNavigation,
    },
    data() {
        return {
            ALL_ISTANBUL_EVENTS,
            VIDEO_SEEK_MODES,
            currentIstanbulEventName: this.selectedIstanbulEventSN,
            currentDayName: this.selectedDaySN,
            beforeFertCheckEventName: null,
            beforeFertCheckRunIndex: null
        }
    },
    computed: {
        ...mapState({
            videoSeekMode: state => state.video.videoSeekMode,
            selectedMKEventSN: state => state.video.selectedMKEventSN,
            selectedIstanbulEventSN: state => state.video.selectedIstanbulEventSN,
            selectedDaySN: state => state.video.selectedDaySN,
            playState: state => state.video.playState,
            skipFramesAmount: state => state.video.skipFramesAmount,
            currentRunIndex: state => state.video.currentRunIndex,
            isFertilizationCheck: state => state.video.isFertilizationCheck,
            selectedTreatment: state => state.timelapse.selectedTreatment,
            currentView: state => state.navigation.currentView,
            selectedSlide: state => state.timelapse.selectedSlide
        }),
        ...mapGetters({
            videoLength_s: 'video/videoLength_s',
            availableMKEvents: 'video/availableMKEvents',
            delayBetweenCycles_m: 'video/delayBetweenCycles_m',
            getStartingHourOfSelectedSlide: 'timelapse/getStartingHourOfSelectedSlide',
            masterWell: 'timelapse/getMasterWell',
            isFdaFriendly: 'common/isFdaFriendly'
        }),
        isMkEventsMode() {
            const { MK_EVENT } = VIDEO_SEEK_MODES

            return this.videoSeekMode === MK_EVENT
        },
        isIstanbulMode() {
            const { ISTANBUL_EVENT } = VIDEO_SEEK_MODES

            return this.videoSeekMode === ISTANBUL_EVENT
        },
        isDayMode() {
            const { DAY } = VIDEO_SEEK_MODES

            return this.videoSeekMode === DAY
        },
        availableEventsNames() {
            const { hoursSinceFertilization } = this.selectedTreatment

            if (this.isMkEventsMode) {
                return this.availableMKEvents
            } else if (this.isIstanbulMode) {
                const availableIstanbulEvents =
                    Utils.filterIstanbulEventsByHoursSinceFertilization(hoursSinceFertilization)

                return availableIstanbulEvents.map(event => event.label)
            } else {
                const availableDays = Utils.filterDaysByHoursSinceFertilization(
                    hoursSinceFertilization,
                    this.getStartingHourOfSelectedSlide
                )

                return availableDays.map(event => event.label)
            }
        },
        selectedEventName() {
            if (this.isFertilizationCheck) return null
            if (this.isMkEventsMode) return this.selectedMKEventSN
            if (this.isIstanbulMode) return this.currentIstanbulEventName
            if (this.isDayMode) return this.currentDayName
            return null
        },
        currentTime_h() {
            if (!this.masterWell) return 0

            const currentRun = this.masterWell.runs[this.currentRunIndex]

            return currentRun ? currentRun.time : 0
        },
        showFertCheckButton() {
            return (
                this.currentView === TREATMENT_PAGE_VIEWS.WELL_CARDS &&
                this.selectedTreatment.status === TREATMENT_STATUS.IN_PROGRESS
            )
        }
    },
    watch: {
        currentTime_h(newVal) {
            if (this.isIstanbulMode) {
                this.syncCurrentIstanbulEvent(newVal)
            } else if (this.isDayMode) {
                this.syncCurrentDay(newVal)
            }
        }
    },
    mounted() {
        this.resetSelectedEvents()
    },
    methods: {
        skip(isForward) {
            if (this.videoSeekMode === VIDEO_SEEK_MODES.TIME) {
                this.$store.dispatch('video/skipFrame_forwardBackward', { isForward })
            } else {
                const targetEventName = Utils.getNextPreviousItem(
                    isForward,
                    this.availableEventsNames,
                    this.selectedEventName
                )

                this.seekWellsToEvent(targetEventName)
            }
            this.setIsFertilizationCheck(false)
        },
        changeSeekMode(mode) {
            this.$store.commit('video/videoSeekMode', mode)
            this.resetSelectedEvents()

            if (mode === VIDEO_SEEK_MODES.TIME) {
                this.$store.commit('video/currentTimeCode_s', null)
                this.$store.dispatch('video/seek', { timecode: this.videoLength_s })
            } else {
                this.seekWellsToEvent(this.availableEventsNames[0])
            }
            this.setIsFertilizationCheck(false)
        },
        seekWellsToEvent(eventName = null) {
            if (this.isMkEventsMode) {
                this.$store.commit('video/selectedMKEventSN', eventName)
            } else if (this.isIstanbulMode) {
                // SynchronizedWellVideo component has watcher on this state need to trigger that watcher
                this.$store.commit('video/selectedIstanbulEventSN', null)
                this.$nextTick(() => {
                    this.$store.commit('video/selectedIstanbulEventSN', eventName)
                })
            } else if (this.isDayMode) {
                // SynchronizedWellVideo component has watcher on this state need to trigger that watcher
                this.$store.commit('video/selectedDaySN', null)
                this.$nextTick(() => {
                    this.$store.commit('video/selectedDaySN', eventName)
                })
            }
            this.setIsFertilizationCheck(false)
        },
        frameSkipAmountChangeHandler(amount) {
            this.$store.state.video.skipFramesAmount = amount
        },
        playHandler() {
            this.$store.dispatch('video/togglePlayStop')
            this.setIsFertilizationCheck(false)
        },
        syncCurrentIstanbulEvent(currentTime_h) {
            const currentIstanbulEvent = ALL_ISTANBUL_EVENTS.find(
                event => event.hours >= currentTime_h
            )

            if (currentIstanbulEvent) {
                this.currentIstanbulEventName = currentIstanbulEvent.label
            }
        },
        syncCurrentDay(currentTime_h) {
            const currentDay = Utils.getDayFromHours(currentTime_h)
            if (currentDay) {
                this.currentDayName = currentDay.label
            }
        },
        setIsFertilizationCheck(isCheck, isReset = false) {
            if (isReset && !isCheck) {
                this.loadSavedState()
            }
            if (isCheck) {
                this.saveCurrentState()
            }
            this.$store.commit('video/isFertilizationCheck', isCheck)
        },
        saveCurrentState() {
            this.beforeFertCheckRunIndex = this.currentRunIndex
            this.beforeFertCheckEventName = this.selectedEventName
        },
        loadSavedState() {
            if (this.isMkEventsMode) {
                this.resetSelectedEvents()
                this.$nextTick(() => {
                    this.seekWellsToEvent(this.beforeFertCheckEventName)
                })
            } else {
                this.$store.dispatch('video/skipToFrame', {
                    frame: 1000
                })
                this.$nextTick(() => {
                    this.$store.dispatch('video/skipToFrame', {
                        frame: this.beforeFertCheckRunIndex
                    })
                })
            }
        },
        resetSelectedEvents() {
            this.$store.commit('video/selectedMKEventSN', null)
            this.$store.commit('video/selectedIstanbulEventSN', null)
            this.$store.commit('video/selectedDaySN', null)
        }
    }
}
</script>

<style lang="scss" scoped>
.synchronized-video-controls {
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: nowrap;
    color: $light;

    .player-navigation {
        margin: 0 1.5rem;
    }

    .fert-check-button {
        margin-right: 1rem;
        min-width: 9.5rem;

        @media screen and #{$new-break2close} {
            display: none;
        }
    }

    @media screen and #{$new-break3} {
        width: 50vw;

        .player-navigation {
            flex-grow: 1;
        }
    }
}
</style>
